import { useEffect, useRef, useState } from "react";
import { getDownloadUrl } from "../data/client";
import { loadPdf, type LoadedPdf, type RenderTask } from "./pdf/loadPdf";
import { canZoom, DEFAULT_ZOOM, fitZoom, scaleFor, stepZoom } from "./pdf/zoom";
import css from "./detail.module.css";

/**
 * The document — PV-1 to PV-10.
 *
 * The chrome around this was drawn months ago and every control was a real,
 * focusable button wired to nothing, over a permanently blank page. That is
 * worse than no chrome: it says the feature is here.
 *
 * The component owns page, zoom and the document. `AssetDetail` keys it on the
 * asset id, so a new asset gets a new instance rather than five pieces of state
 * that each have to remember to reset (PV-10) — the same trick, and the same
 * reasoning, as `patron-preview` P-6.
 */

type State =
  | { phase: "loading" }
  /** Previewing. Not a failure — a thing preview deliberately does not do. */
  | { phase: "preview" }
  /** The server refused the URL: this tier does not hold the slot. */
  | { phase: "refused" }
  | { phase: "failed"; code: string }
  | { phase: "ready"; pdf: LoadedPdf };

export function PdfViewer({
  assetId,
  title,
  expanded,
  onExpandedChange,
  /**
   * True while the owner is impersonating a tier.
   *
   * Passed in rather than read from context here so this component has no
   * opinion about *why* it may not fetch — and so the test for AC-5 can state
   * the case without building a preview session around it.
   */
  previewing,
}: {
  assetId: string;
  title: string;
  previewing: boolean;
  /**
   * Fullscreen is **controlled from above** — R-D5.
   *
   * The viewer used to own this and listen for its own `Escape`. It cannot any
   * more: R-14 wants the first `Esc` to leave fullscreen and the second to leave
   * the tab, and two listeners for one key means one press does both. So the
   * screen owns the listener and this owns the rendering.
   */
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}) {
  const [state, setState] = useState<State>({ phase: "loading" });
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const matRef = useRef<HTMLDivElement | null>(null);
  /*
   * Expanded **within the page** — not the Fullscreen API.
   *
   * PV-4 originally said to call `requestFullscreen()`, and that was wrong in
   * three ways at once, all reported on 2026-08-31: on macOS it takes the whole
   * browser window into native fullscreen, not just this viewer; leaving that
   * native fullscreen leaves the viewer still expanded, because the app never
   * hears about it; and the button went on saying FULLSCREEN with no way back
   * out of it from our own UI.
   *
   * The overlay mechanism survived that report unchanged. What moved since is
   * where the state lives (R-D5) and what the overlay shows (R-13).
   */
  const setExpanded = onExpandedChange;
  /** The zoom to return to. Expanding changes it; collapsing must not lose it. */
  const zoomBeforeExpand = useRef<number | null>(null);

  /* ---- open the document ------------------------------------------------ */
  useEffect(() => {
    /*
     * AC-5: no request at all while previewing.
     *
     * Not "request it and handle the 403". `patron-preview` FR-8 is that the
     * server issues no signed URL in preview; asking anyway would mean every
     * previewed asset produced a refused grant in the logs, and it would make a
     * genuine 403 - a real entitlement failure - indistinguishable from the
     * expected one.
     */
    if (previewing) {
      setState({ phase: "preview" });
      return;
    }

    let live = true;
    setState({ phase: "loading" });

    getDownloadUrl(assetId, "pdf")
      .then((url) => loadPdf(url))
      .then((pdf) => {
        if (!live) {
          // Nobody is going to read it, and pdf.js holds a worker until told.
          void pdf.destroy();
          return;
        }
        setState({ phase: "ready", pdf });
      })
      .catch((error: Error) => {
        if (!live) return;
        /*
         * A refusal is the expected answer for a tier that does not hold this
         * slot, and it is not an error state - the rail says the same thing
         * beside it. Anything else is genuinely wrong and carries a code.
         */
        const message = String(error?.message ?? "");
        if (/403|forbidden/i.test(message) || error?.name === "ForbiddenError") {
          setState({ phase: "refused" });
          return;
        }
        setState({ phase: "failed", code: codeFor(error) });
      });

    return () => {
      live = false;
    };
  }, [assetId, previewing]);

  /** Close the document when the viewer goes away, not when React feels like it. */
  useEffect(() => {
    if (state.phase !== "ready") return;
    const { pdf } = state;
    return () => {
      void pdf.destroy();
    };
  }, [state]);

  /* ---- render the current page ------------------------------------------ */
  useEffect(() => {
    if (state.phase !== "ready") return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    let cancelled = false;
    let task: RenderTask | undefined;

    (async () => {
      try {
        const pdfPage = await state.pdf.doc.getPage(page);
        if (cancelled) return;

        /*
         * PV-3: re-rendered at the new scale, not scaled with CSS.
         *
         * A `transform` looks right on screen and is a blurry mess the moment
         * anyone zooms in to read a chord voicing - which is the entire reason
         * someone reaches for zoom on a tab.
         */
        const viewport = pdfPage.getViewport({ scale: scaleFor(zoom) });
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.width = `${Math.floor(viewport.width)}px`;

        // v6 takes the canvas itself; `canvasContext` is the compatibility
        // path and requires `canvas: null` alongside it.
        task = pdfPage.render({ canvas, viewport });
        await task.promise;
      } catch (error) {
        // A cancelled render rejects. That is this effect's own cleanup doing
        // its job, not a failure to report.
        if (cancelled || (error as { name?: string })?.name === "RenderingCancelledException") {
          return;
        }
        setState({ phase: "failed", code: codeFor(error) });
      }
    })();

    /*
     * PV-NFR-4 / AC-10. Without this, a render started for page 1 that lands
     * after the reader has pressed `▶` paints page 1 over page 2 - which reads
     * as a rendering glitch and is a race. The flag alone is not enough: the
     * render is already running in the worker and has to be told.
     */
    return () => {
      cancelled = true;
      task?.cancel();
    };
  }, [state, page, zoom]);

  const total = state.phase === "ready" ? state.pdf.doc.numPages : 0;

  const toggleExpanded = () => {
    if (expanded) {
      collapse();
      return;
    }

    /*
     * Fit the page to the room it is about to get. Expanding without this
     * gives a bigger desk and the same small page, which is not what anyone
     * means by the word on the button.
     *
     * Measured from the canvas's own current height rather than asking pdf.js
     * again: height / (zoom/100) is the page at 100%, which is what `fitZoom`
     * wants. In jsdom every measurement is 0 and `fitZoom` returns the default,
     * so this degrades to "expand, keep the zoom".
     */
    const canvas = canvasRef.current;
    const heightAtFullSize = canvas ? canvas.clientHeight / scaleFor(zoom) : 0;
    const available = window.innerHeight - EXPANDED_CHROME_ALLOWANCE;

    zoomBeforeExpand.current = zoom;
    setZoom(fitZoom(heightAtFullSize, available));
    setExpanded(true);
  };

  /*
   * No `Escape` listener here — R-D5.
   *
   * There was one, and it had to go. `AssetDetail` owns the key now, because
   * R-14 wants the first press to leave fullscreen and the second to leave the
   * tab: two listeners on one event means one press does both, and which
   * "first" wins depends on the order React attached them.
   */

  /** Leave the overlay and give back the zoom the reader had chosen. */
  function collapse() {
    setExpanded(false);
    if (zoomBeforeExpand.current !== null) setZoom(zoomBeforeExpand.current);
    zoomBeforeExpand.current = null;
  }

  /*
   * Hold the page behind still. Without it a scroll gesture over the overlay
   * moves the library underneath, and leaving the overlay puts the reader
   * somewhere they never navigated to.
   */
  useEffect(() => {
    if (!expanded) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // R-13: takes the app header out of the document, not merely out of view -
    // covered-but-focusable is how a reader tabs into links they cannot see.
    document.body.classList.add("viewer-fullscreen");
    return () => {
      document.body.style.overflow = previous;
      document.body.classList.remove("viewer-fullscreen");
    };
  }, [expanded]);

  return (
    <section
      className={expanded ? `${css.viewer} ${css.viewerExpanded}` : css.viewer}
      ref={viewerRef}
    >
      {/*
        R-13: fullscreen hides **all** chrome, this bar included. What replaces
        it is one labelled chip over the mat — not a floating X, which in the
        corner of content reads as a modal dismiss, and this is a page.
      */}
      {expanded ? (
        <button type="button" className={css.exitChip} onClick={collapse}>
          <span aria-hidden="true">&#8249;</span> EXIT FULLSCREEN
        </button>
      ) : (
      <div className={css.chrome}>
        <div className={css.chromeGroup}>
          <button
            type="button"
            className={css.chromeButton}
            aria-label="Previous page"
            disabled={state.phase !== "ready" || page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ◀
          </button>
          <span className={css.chromeLabel}>
            {/* PV-2: the total comes from the document. `pdf.pages` on the file
                record is whatever was recorded at upload, and this is the
                number that is actually true. */}
            PAGE {state.phase === "ready" ? page : "—"} / {total || "—"}
          </span>
          <button
            type="button"
            className={css.chromeButton}
            aria-label="Next page"
            disabled={state.phase !== "ready" || page >= total}
            onClick={() => setPage((p) => Math.min(total, p + 1))}
          >
            ▶
          </button>
        </div>

        <div className={`${css.chromeGroup} ${css.chromeGroupRight}`}>
          <button
            type="button"
            className={css.chromeButton}
            aria-label="Zoom out"
            disabled={state.phase !== "ready" || !canZoom(zoom, -1)}
            onClick={() => setZoom((z) => stepZoom(z, -1))}
          >
            −
          </button>
          <span className={css.chromeLabel}>{zoom}%</span>
          <button
            type="button"
            className={css.chromeButton}
            aria-label="Zoom in"
            disabled={state.phase !== "ready" || !canZoom(zoom, 1)}
            onClick={() => setZoom((z) => stepZoom(z, 1))}
          >
            +
          </button>
          <button
            type="button"
            className={css.chromeButton}
            aria-pressed={expanded}
            disabled={state.phase !== "ready"}
            onClick={toggleExpanded}
          >
            FULLSCREEN
          </button>
        </div>
      </div>
      )}

      {/* NFR-9 / PV-9: untinted, unstyled, nothing pixel inside it. */}
      <div className={css.mat} ref={matRef}>
        {state.phase === "ready" ? (
          <canvas
            ref={canvasRef}
            className={css.page660}
            // PV-NFR-2: a canvas is opaque to a screen reader, so it says what
            // it is holding. It cannot say what the notation *is* - that is
            // what the download is for.
            role="img"
            aria-label={`${title} — page ${page} of ${total}`}
          />
        ) : (
          <div className={css.page660}>
            <p className={css.docState}>{copyFor(state)}</p>
            {state.phase === "failed" && (
              <p className={css.docCode}>{state.code}</p>
            )}
          </div>
        )}
      </div>

    </section>
  );
}

/** Chrome bar, mat padding and a little breathing room, in CSS pixels. */
const EXPANDED_CHROME_ALLOWANCE = 140;

/** PV-7: every state names itself. None of them is a blank page. */
function copyFor(state: State): string {
  switch (state.phase) {
    case "loading":
      return "OPENING THE DOCUMENT…";
    case "preview":
      // Not an apology and not a failure. Preview answers "what is visible",
      // and the owner's own session is where a document gets checked
      // (patron-preview FR-8).
      return "PREVIEW DOES NOT FETCH DOCUMENTS. OPEN IT AS YOURSELF TO READ IT.";
    case "refused":
      return "THIS TAB IS NOT IN YOUR TIER.";
    case "failed":
      return "THIS DOCUMENT DID NOT OPEN.";
    default:
      return "";
  }
}

function codeFor(error: unknown): string {
  const name = (error as { name?: string })?.name;
  const message = (error as { message?: string })?.message;
  // `PDF_FETCH_404` and friends come from loadPdf and are already a code.
  if (message && /^PDF_[A-Z0-9_]+$/.test(message)) return message;
  return `PDF_RENDER_FAILED · ${(name ?? "ERROR").toUpperCase()}`;
}
